import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import { ReactElement, useEffect, useRef, useState } from 'react';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { FilterTerm } from '@/pages/Search/Components/FilterTerm';
import { useAppSelector } from '@/redux/hooks';
import { getLocale } from '@/redux/modules/intl';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

export type AuctionsNearMeFilterBarProps = {
    numberOfAuctions: number;
    onKeywordsChange: (keywords: string[]) => void;
    toggleLocalPickUpFilter: () => void;
    toggleTopRatedFilter: () => void;
};

export const AuctionsNearMeFilterBar = ({
    numberOfAuctions,
    onKeywordsChange,
    toggleLocalPickUpFilter,
    toggleTopRatedFilter,
}: AuctionsNearMeFilterBarProps): ReactElement => {
    const formatMessage = useAppFormatMessage();
    const locale = useAppSelector(getLocale);
    const filterByKeywordPlaceholder = String(formatMessage(['filter_by_keyword']));
    const [keywords, setKeywords] = useState<string[]>([]);
    const isMobile = useIsMobileLayout();

    // Skip the initial mount pass: `keywords` starts empty, matching the parent's own default,
    // so there is nothing to propagate yet. Calling `onKeywordsChange` on mount cascades into the
    // parent's "re-fetch on filter/pagination change" effect landing on its second render (after
    // that hook's own mount-skip ref has already flipped), firing a spurious bounds-based re-fetch
    // that overwrites the precise SSR result on Auctions Near Me pages with no GeoArea (SEAR-1816).
    const hasMountedRef = useRef(false);

    const addKeyword = (keyword: string): void => {
        const keywordAlreadyExists = keywords.find(
            (existingKeyword) => existingKeyword.toLowerCase() === keyword.toLowerCase()
        );
        if (!keywordAlreadyExists) {
            setKeywords([...keywords, keyword]);
        }
    };

    const removeKeyword = (keyword: string): void => {
        const keywordsClone = keywords.slice();
        keywordsClone.splice(keywordsClone.indexOf(keyword), 1);
        setKeywords(keywordsClone);
    };

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        onKeywordsChange(keywords);
    }, [keywords, onKeywordsChange]);

    const hasKeywordFilters = keywords.length > 0;

    const desktopLayout = !isMobile ? (
        <span className="smMin:mt-[12px] smMin:flex smMin:flex-wrap [&_[data-testid='select']]:w-[90px] [&_[data-testid='text']]:inline-block [&_[data-testid='text']]:align-middle [&_[data-testid='text']]:font-primary [&_[data-testid='text']]:text-xs [&_[data-testid='text']]:leading-[18px] [&_[data-testid='text']]:tracking-[1px] [&_[data-testid='text']]:text-text-inactive">
            <span className="mt-xs flex">
                <FilterTerm
                    placeholderMessage={filterByKeywordPlaceholder}
                    shortOnMobile
                    submitFilterTerm={addKeyword}
                />
                <span className="flex xsMax:mt-xs">
                    <Chip onClick={toggleTopRatedFilter}>{formatMessage(['topRatedCount'])}</Chip>
                    <Chip onClick={toggleLocalPickUpFilter}>{formatMessage(['freeLocalPickupCount'])}</Chip>
                </span>
            </span>
            {hasKeywordFilters && (
                <>
                    {keywords.map((keyword) => (
                        <div
                            className="mt-xs"
                            key={`auctions-near-me-keyword-filter-${keyword}`}
                        >
                            <Chip
                                aria-label={`Remove ${keyword} filter`}
                                className="max-w-[77px]"
                                key={`auctions-near-me-keyword-filter-${keyword}`}
                                onClose={() => removeKeyword(keyword)}
                                selected
                                style="input"
                            >
                                {keyword}
                            </Chip>
                        </div>
                    ))}
                </>
            )}
        </span>
    ) : null;

    const mobileLayout = isMobile ? (
        <span>
            <div className="mt-xs">
                <FilterTerm
                    placeholderMessage={filterByKeywordPlaceholder}
                    shortOnMobile
                    submitFilterTerm={addKeyword}
                />
                <span className="flex xsMax:mt-xs">
                    <Chip onClick={toggleTopRatedFilter}>{formatMessage(['topRatedCount'])}</Chip>
                    <Chip onClick={toggleLocalPickUpFilter}>{formatMessage(['freeLocalPickupCount'])}</Chip>
                </span>
            </div>
            {hasKeywordFilters && (
                <div className="mb-xs flex flex-wrap">
                    {keywords.map((keyword) => (
                        <div className="mt-xs">
                            <Chip
                                aria-label={`Remove ${keyword} filter`}
                                className="max-w-[77px]"
                                key={`auctions-near-me-keyword-filter-${keyword}`}
                                onClose={() => removeKeyword(keyword)}
                                selected
                                style="input"
                            >
                                {keyword}
                            </Chip>
                        </div>
                    ))}
                </div>
            )}
        </span>
    ) : null;

    return (
        <section
            className="flex flex-col content-center smMin:mt-0 xsMax:mt-[20px]"
            data-testid="auctionsSection"
        >
            <div>
                <div className="mr-xs flex-1 self-center xsMax:mt-lg">
                    {isMobile ? (
                        <H2 data-testid="auctionCount">
                            {numberOfAuctions === 1
                                ? formatMessage(['nearby_auctions_count_single'])
                                : formatMessage(['nearby_auctions_count'], {
                                      values: {
                                          numberOfAuctions: Intl.NumberFormat(locale).format(numberOfAuctions),
                                      },
                                  })}
                        </H2>
                    ) : (
                        <H3 data-testid="auctionCount">
                            {numberOfAuctions === 1
                                ? formatMessage(['nearby_auctions_count_single'])
                                : formatMessage(['nearby_auctions_count'], {
                                      values: {
                                          numberOfAuctions: Intl.NumberFormat(locale).format(numberOfAuctions),
                                      },
                                  })}
                        </H3>
                    )}
                </div>
            </div>
            {desktopLayout || mobileLayout}
        </section>
    );
};
